<template>
  <div class="driver-detail-container">
    <!-- 顶部导航栏 -->
    <div class="page-header">
      <div class="header-left">
        <el-button icon="el-icon-arrow-left" size="small" @click="goBack">返回</el-button>
        <span class="page-title">驾驶员详情</span>
      </div>
      <div class="header-right">
        <!-- <el-button 
          type="primary" 
          size="small" 
          icon="el-icon-edit" 
          @click="goEdit" 
          v-if="driverInfo && driverInfo.id"
        >编辑</el-button> -->
        <el-button 
          type="success" 
          size="small" 
          icon="el-icon-refresh-right" 
          @click="fetchDriverDetail"
          :loading="loading"
        >刷新</el-button>
      </div>
    </div>

    <!-- 主要内容区 -->
    <el-card class="detail-card" v-loading="loading" shadow="hover">
      <div v-if="driverInfo" class="driver-content">
        <!-- 状态横幅 -->
        <div class="status-banner" :class="driverInfo.status === 0 ? 'status-active' : 'status-inactive'">
          <div class="status-icon">
            <i :class="driverInfo.status === 0 ? 'el-icon-check' : 'el-icon-close'"></i>
          </div>
          <div class="status-text">
            <span class="status-label">当前状态</span>
            <span class="status-value">{{ driverInfo.status === 0 ? '在职' : '离职' }}</span>
          </div>
          <div class="status-actions">
            <el-tag :type="getExpireTagType(driverInfo.isLicenseExpired, driverInfo.isLicenseExpiringSoon)" size="small">
              驾驶证 {{ getDaysRemaining(driverInfo.licenseExpireDate) }}
            </el-tag>
            <el-tag :type="getExpireTagType(driverInfo.isQualificationExpired, driverInfo.isQualificationExpiringSoon)" size="small">
              资格证 {{ getDaysRemaining(driverInfo.qualificationExpireDate) }}
            </el-tag>
          </div>
        </div>

        <!-- 关键信息卡片 -->
        <div class="info-cards">
          <div class="info-card">
            <div class="card-icon">
              <i class="el-icon-user"></i>
            </div>
            <div class="card-content">
              <div class="card-label">姓名</div>
              <div class="card-value">{{ driverInfo.name }}</div>
            </div>
          </div>
          <div class="info-card">
            <div class="card-icon">
              <i class="el-icon-postcard"></i>
            </div>
            <div class="card-content">
              <div class="card-label">准驾车型</div>
              <div class="card-value">{{ driverInfo.licenseType || '—' }}</div>
            </div>
          </div>
          <div class="info-card">
            <div class="card-icon">
              <i class="el-icon-id-card"></i>
            </div>
            <div class="card-content">
              <div class="card-label">身份证号</div>
              <div class="card-value">{{ maskIdCard(driverInfo.idCardNumber) || '—' }}</div>
            </div>
          </div>
          <div class="info-card">
            <div class="card-icon">
              <i class="el-icon-office-building"></i>
            </div>
            <div class="card-content">
              <div class="card-label">档案编号</div>
              <div class="card-value">{{ driverInfo.archiveNumber || '—' }}</div>
            </div>
          </div>
        </div>

        <!-- 基本信息 -->
        <div class="section">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-info"></i>
              <span>基本信息</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">驾驶员ID</span>
              <span class="info-value">{{ driverInfo.id }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">姓名</span>
              <span class="info-value">{{ driverInfo.name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">身份证号</span>
              <span class="info-value">{{  maskIdCard(driverInfo.idCardNumber) || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">准驾车型</span>
              <span class="info-value">{{ driverInfo.licenseType || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">档案编号</span>
              <span class="info-value">{{ driverInfo.archiveNumber || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">在职状态</span>
              <span class="info-value">
                <el-tag :type="driverInfo.status === 0 ? 'success' : 'info'" size="small">
                  {{ driverInfo.status === 0 ? '在职' : '离职' }}
                </el-tag>
              </span>
            </div>
          </div>
        </div>

        <!-- 证件有效期 -->
        <div class="section">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-date"></i>
              <span>证件有效期</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">驾驶证注册日期</span>
              <span class="info-value">{{ formatDate(driverInfo.licenseRegisterDate) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">驾驶证有效期</span>
              <span class="info-value" :class="{ 'expiry-warning': driverInfo.isLicenseExpired, 'expiry-soon': driverInfo.isLicenseExpiringSoon }">
                {{ formatDate(driverInfo.licenseExpireDate) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">资格证有效期</span>
              <span class="info-value" :class="{ 'expiry-warning': driverInfo.isQualificationExpired, 'expiry-soon': driverInfo.isQualificationExpiringSoon }">
                {{ formatDate(driverInfo.qualificationExpireDate) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">体检到期日</span>
              <span class="info-value" :class="{ 'expiry-warning': isExpired(driverInfo.medicalExpireDate) }">
                {{ formatDate(driverInfo.medicalExpireDate) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">无犯罪记录到期日</span>
              <span class="info-value" :class="{ 'expiry-warning': isExpired(driverInfo.criminalRecordExpireDate) }">
                {{ formatDate(driverInfo.criminalRecordExpireDate) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">安全培训日期</span>
              <span class="info-value">{{ formatDate(driverInfo.safetyTrainingDate) }}</span>
            </div>
          </div>
        </div>

        <!-- 审核信息 -->
        <div class="section">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-s-check"></i>
              <span>审核信息</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">审核状态</span>
              <span class="info-value">
                <el-tag :type="getAuditStatusType(driverInfo.auditStatus)" size="small">
                  {{ getAuditStatusText(driverInfo.auditStatus) }}
                </el-tag>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">审核时间</span>
              <span class="info-value">{{ formatDateTime(driverInfo.auditTime) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">审核人</span>
              <span class="info-value">{{ driverInfo.auditor || '—' }}</span>
            </div>
            <div class="info-row" v-if="driverInfo.auditRemark">
              <span class="info-label">审核意见</span>
              <span class="info-value">{{ driverInfo.auditRemark }}</span>
            </div>
          </div>
        </div>

        <!-- 关联车牌 -->
        <div class="section" v-if="plateList && plateList.length > 0">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-truck"></i>
              <span>关联车辆</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="plate-tags">
            <el-tag 
              v-for="plate in plateList" 
              :key="plate"
              type="primary"
              size="medium"
              class="plate-tag"
            >
              {{ plate }}
            </el-tag>
          </div>
        </div>

        <!-- 照片区域 -->
        <div class="section" v-if="hasPhotos">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-picture"></i>
              <span>证件照片</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="photo-grid">
            <div v-if="driverInfo.idCardPhotoUrl" class="photo-item">
              <span class="photo-label">身份证照片</span>
              <ImageViewer :src="driverInfo.idCardPhotoUrl" class="detail-photo" />
            </div>
            <div v-if="driverInfo.driverLicensePhotoUrl" class="photo-item">
              <span class="photo-label">驾驶证照片</span>
              <ImageViewer :src="driverInfo.driverLicensePhotoUrl" class="detail-photo" />
            </div>
            <div v-if="driverInfo.facePhotoUrl" class="photo-item">
              <span class="photo-label">人脸照片</span>
              <ImageViewer :src="driverInfo.facePhotoUrl" class="detail-photo" />
            </div>
            <div v-for="(url, key) in otherPhotos" :key="key" class="photo-item">
              <span class="photo-label">其他材料 {{ key.replace('image', '') }}</span>
              <ImageViewer :src="url" class="detail-photo" />
            </div>
          </div>
        </div>

        <!-- 备注 -->
        <div class="section" v-if="driverInfo.remark">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-document"></i>
              <span>婚配及家庭情况：</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="remark-content">
            {{ driverInfo.remark }}
          </div>
        </div>

        <!-- 记录信息 -->
        <div class="section">
          <div class="section-header">
            <div class="section-title">
              <i class="el-icon-time"></i>
              <span>记录信息</span>
            </div>
            <div class="section-line"></div>
          </div>
          <div class="info-grid">
            <div class="info-row">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ formatDateTime(driverInfo.createdAt) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">创建人</span>
              <span class="info-value">{{ driverInfo.createdBy || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">更新时间</span>
              <span class="info-value">{{ formatDateTime(driverInfo.updatedAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from '../utils/api'
import ImageViewer from '../components/ImageViewer.vue'

export default {
  name: 'DriverDetail',
  components: {
    ImageViewer
  },
  data() {
    return {
      loading: false,
      driverInfo: null,
      driverId: null,
      plateList: []
    }
  },
  computed: {
    hasPhotos() {
      if (!this.driverInfo) return false
      return this.driverInfo.idCardPhotoUrl || 
             this.driverInfo.driverLicensePhotoUrl || 
             this.driverInfo.facePhotoUrl ||
             Object.keys(this.otherPhotos).length > 0
    },
    otherPhotos() {
      if (!this.driverInfo || !this.driverInfo.otherPhotoUrl) return {}
      try {
        const parsed = JSON.parse(this.driverInfo.otherPhotoUrl)
        // 过滤出有值的图片
        const result = {}
        Object.keys(parsed).forEach(key => {
          if (parsed[key] && parsed[key].trim() !== '') {
            result[key] = parsed[key]
          }
        })
        return result
      } catch (e) {
        return {}
      }
    }
  },
  mounted() {
    this.driverId = this.$route.query.id
    if (this.driverId) {
      this.fetchDriverDetail()
    }
  },
  methods: {
    async fetchDriverDetail() {
      if (!this.driverId) return
      this.loading = true
      try {
        const res = await api.get(`/api/Drivers/${this.driverId}`)
        this.driverInfo = res.data
        // 解析关联车牌
        if (this.driverInfo.plateList) {
          this.plateList = this.driverInfo.plateList
        } else if (this.driverInfo.associatedPlates) {
          this.plateList = this.driverInfo.associatedPlates.split(',').map(s => s.trim()).filter(Boolean)
        }
      } catch (error) {
        this.$message.error('获取驾驶员信息失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.back()
    },
    goEdit() {
      this.$router.push(`/driver/edit/${this.driverId}`)
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      try {
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return dateStr
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      } catch (e) {
        return dateStr
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '—'
      try {
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return '—'
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return '—'
      }
    },
    getDaysRemaining(dateStr) {
      if (!dateStr) return '未设置'
      try {
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return '无效日期'
        const now = new Date()
        const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24))
        if (diff < 0) return `已过期 ${Math.abs(diff)} 天`
        if (diff === 0) return '今天到期'
        return `剩余 ${diff} 天`
      } catch (e) {
        return '未知'
      }
    },
    isExpired(dateStr) {
      if (!dateStr) return false
      try {
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) return false
        return date < new Date()
      } catch (e) {
        return false
      }
    },
    getExpireTagType(isExpired, isExpiringSoon) {
      if (isExpired) return 'danger'
      if (isExpiringSoon) return 'warning'
      return 'success'
    },
    getAuditStatusType(auditStatus) {
      // 0=Pending, 1=Approved, 2=Rejected
      switch (auditStatus) {
        case 1: return 'success'
        case 2: return 'danger'
        default: return 'warning'
      }
    },
    getAuditStatusText(auditStatus) {
      switch (auditStatus) {
        case 0: return '待审核'
        case 1: return '已通过'
        case 2: return '已拒绝'
        default: return '未知'
      }
    },
    maskIdCard(idCard) {
      if (!idCard) return ''
      return idCard.replace(/^(.{4})(.*)(.{4})$/, '$1**********$3')
    }
  }
}
</script>

<style scoped>
.driver-detail-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
}

.detail-card {
  border-radius: 12px;
  overflow: hidden;
}

.status-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  margin: -1px -1px 20px -1px;
  border-radius: 12px 12px 0 0;
}

.status-active {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
}

.status-inactive {
  background: linear-gradient(135deg, #f56c6c 0%, #f89898 100%);
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 24px;
}

.status-text {
  display: flex;
  flex-direction: column;
  color: #ffffff;
}

.status-label {
  font-size: 12px;
  opacity: 0.85;
}

.status-value {
  font-size: 20px;
  font-weight: 600;
}

.status-actions {
  display: flex;
  gap: 8px;
}

.info-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.info-card {
  flex: 1;
  min-width: 200px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e2e8f0;
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 20px;
}

.card-content {
  display: flex;
  flex-direction: column;
}

.card-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.card-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.section {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}

.section-title i {
  color: #667eea;
}

.section-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  margin-left: 12px;
  border-radius: 1px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.info-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
}

.info-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
  text-align: right;
}

.info-value.expiry-warning {
  color: #dc2626;
  font-weight: 600;
}

.info-value.expiry-soon {
  color: #f59e0b;
  font-weight: 600;
}

.plate-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 0;
}

.plate-tag {
  font-size: 14px;
  padding: 6px 14px;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.photo-item {
  width: calc(33.333% - 12px);
  min-width: 200px;
}

.photo-label {
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
}

.detail-photo {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.remark-content {
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
}

::v-deep .el-card__header {
  display: none;
}

::v-deep .el-button--small {
  padding: 8px 16px;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .photo-item {
    width: calc(50% - 8px);
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>